package com.bulut.mecitogullari.ui.screens.craftsmandetail

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.export.CraftsmanReportData
import com.bulut.mecitogullari.core.export.CraftsmanReportHtml
import com.bulut.mecitogullari.core.export.safePdfName
import com.bulut.mecitogullari.core.platform.isPrintSupported
import com.bulut.mecitogullari.core.platform.printHtml
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.core.util.formatDateShort
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.CraftsmanEntry
import com.bulut.mecitogullari.data.model.CraftsmanEntryType
import com.bulut.mecitogullari.ui.components.AmountField
import com.bulut.mecitogullari.ui.components.DateField
import com.bulut.mecitogullari.ui.components.DropdownField
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.components.SectionTitle
import com.bulut.mecitogullari.ui.screens.craftsmen.formatWork
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.math.abs

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CraftsmanDetailScreen(
    craftsmanId: String,
    onBack: () -> Unit,
    viewModel: CraftsmanDetailViewModel = koinViewModel { parametersOf(craftsmanId) },
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val craftsmanDeleted by viewModel.craftsmanDeleted.collectAsStateWithLifecycle()
    var showWorkDialog by remember { mutableStateOf(false) }
    var showEntryDialog by remember { mutableStateOf(false) }
    var showInvoiceDialog by remember { mutableStateOf(false) }
    var showGiveUnitDialog by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var unitToTakeBack by remember { mutableStateOf<ApartmentUnit?>(null) }
    var entryToDelete by remember { mutableStateOf<CraftsmanEntry?>(null) }
    val strings = LocalStrings.current

    LaunchedEffect(craftsmanDeleted) {
        if (craftsmanDeleted) onBack()
    }

    val craftsman = uiState.craftsman
    val balance = uiState.balance

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(craftsman?.name ?: strings.craftsman) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    if (isPrintSupported && craftsman != null && balance != null) {
                        IconButton(
                            onClick = {
                                printHtml(
                                    html = CraftsmanReportHtml.buildSingle(
                                        data = CraftsmanReportData(
                                            craftsman = craftsman,
                                            balance = balance,
                                            entries = uiState.entries,
                                            unitsGiven = uiState.unitsGiven,
                                        ),
                                        projectNames = uiState.projectNames,
                                    ),
                                    fileName = "usta-${safePdfName(craftsman.name)}.pdf",
                                )
                            },
                        ) {
                            Icon(Icons.Default.Share, contentDescription = strings.downloadPdf)
                        }
                    }
                    IconButton(onClick = { showDeleteConfirm = true }) {
                        Icon(Icons.Default.Delete, contentDescription = strings.deleteCraftsmanAction, tint = AppColors.Expense)
                    }
                },
            )
        },
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding),
            contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            if (craftsman != null && balance != null) {
                item {
                    BalanceCard(
                        craftsman = craftsman,
                        balance = balance,
                    )
                }

                item {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Button(
                            onClick = { showWorkDialog = true },
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                            contentPadding = PaddingValues(horizontal = 6.dp),
                            modifier = Modifier.weight(1f).height(44.dp),
                        ) {
                            Text(strings.updateWork, maxLines = 1, overflow = TextOverflow.Ellipsis)
                        }
                        OutlinedButton(
                            onClick = { showGiveUnitDialog = true },
                            shape = RoundedCornerShape(12.dp),
                            contentPadding = PaddingValues(horizontal = 6.dp),
                            modifier = Modifier.weight(1f).height(44.dp),
                        ) {
                            Text(strings.giveUnitAction, maxLines = 1, overflow = TextOverflow.Ellipsis)
                        }
                        OutlinedButton(
                            onClick = { showEntryDialog = true },
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.outlinedButtonColors(contentColor = AppColors.Income),
                            contentPadding = PaddingValues(horizontal = 6.dp),
                            modifier = Modifier.weight(1f).height(44.dp),
                        ) {
                            Text(strings.entryAction, maxLines = 1, overflow = TextOverflow.Ellipsis)
                        }
                    }
                }

                item {
                    InvoiceCard(
                        required = balance.requiredInvoice,
                        invoiced = balance.invoiced,
                        onClick = { showInvoiceDialog = true },
                    )
                }

                if (uiState.unitsGiven.isNotEmpty()) {
                    item { SectionTitle(strings.givenUnitsSection) }
                    items(uiState.unitsGiven, key = { "given-${it.id}" }) { unit ->
                        GivenUnitRow(
                            unit = unit,
                            projectName = uiState.projectNames[unit.projectId] ?: "",
                            onClick = { unitToTakeBack = unit },
                        )
                    }
                }

                item { SectionTitle(strings.accountEntriesSection) }
                if (uiState.entries.isEmpty()) {
                    item {
                        EmptyState(strings.noEntriesYet)
                    }
                } else {
                    items(uiState.entries, key = { it.id }) { entry ->
                        EntryRow(entry = entry, onClick = { entryToDelete = entry })
                    }
                }
            }
        }
    }

    if (showWorkDialog && craftsman != null) {
        WorkProgressDialog(
            workTypes = craftsman.effectiveWorkTypes.filter { it.agreedCount > 0 },
            onDismiss = { showWorkDialog = false },
            onSave = { completed ->
                viewModel.updateCompletedWork(completed)
                showWorkDialog = false
            },
        )
    }

    if (showEntryDialog) {
        CraftsmanEntryDialog(
            onDismiss = { showEntryDialog = false },
            onSave = { type, amount, date, description ->
                viewModel.addEntry(type, amount, date, description)
                showEntryDialog = false
            },
        )
    }

    if (showInvoiceDialog && balance != null) {
        InvoiceTargetDialog(
            currentRequired = balance.requiredInvoice,
            onDismiss = { showInvoiceDialog = false },
            onSave = { amount ->
                viewModel.updateRequiredInvoice(amount)
                showInvoiceDialog = false
            },
        )
    }

    if (showGiveUnitDialog) {
        GiveUnitDialog(
            projects = uiState.projects.filter { project ->
                uiState.availableUnits.any { it.projectId == project.id }
            },
            availableUnits = uiState.availableUnits,
            onDismiss = { showGiveUnitDialog = false },
            onGive = { unit, value ->
                viewModel.giveUnit(unit, value)
                showGiveUnitDialog = false
            },
        )
    }

    unitToTakeBack?.let { unit ->
        AlertDialog(
            onDismissRequest = { unitToTakeBack = null },
            title = { Text(strings.takeBackUnitTitle) },
            text = {
                Text(strings.takeBackUnitMessage.fillTemplate("unit" to unitDisplayLabel(unit.block, unit.number)))
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        viewModel.takeBackUnit(unit)
                        unitToTakeBack = null
                    },
                ) {
                    Text(strings.takeBack, color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { unitToTakeBack = null }) { Text(strings.cancel) }
            },
        )
    }

    entryToDelete?.let { entry ->
        AlertDialog(
            onDismissRequest = { entryToDelete = null },
            title = { Text(strings.deleteEntryTitle) },
            text = {
                Text(
                    strings.deleteEntryMessage.fillTemplate(
                        "desc" to entry.description,
                        "amount" to formatAmount(entry.amount),
                    )
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        viewModel.deleteEntry(entry.id)
                        entryToDelete = null
                    },
                ) {
                    Text(strings.delete, color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { entryToDelete = null }) { Text(strings.cancel) }
            },
        )
    }

    if (showDeleteConfirm) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            title = { Text(strings.deleteCraftsmanTitle) },
            text = {
                Text(
                    strings.deleteCraftsmanMessage
                        .fillTemplate("name" to craftsman?.name.orEmpty())
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteConfirm = false
                        viewModel.deleteCraftsman()
                    },
                ) {
                    Text("Sil", color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirm = false }) { Text(strings.cancel) }
            },
        )
    }
}

@Composable
private fun BalanceCard(
    craftsman: com.bulut.mecitogullari.data.model.Craftsman,
    balance: com.bulut.mecitogullari.data.model.CraftsmanBalance,
) {
    val strings = LocalStrings.current
    val net = balance.currentNet
    Card(
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = AppColors.NavyDeep),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text(
                text = when {
                    net > 0 -> strings.craftsmanOwedTitle
                    net < 0 -> strings.craftsmanOwesTitle
                    else -> strings.accountBalancedTitle
                },
                style = MaterialTheme.typography.labelLarge,
                color = Color.White.copy(alpha = 0.8f),
            )
            Text(
                text = formatAmount(abs(net)),
                style = MaterialTheme.typography.headlineMedium,
                color = when {
                    net > 0 -> Color(0xFFF87171)
                    net < 0 -> Color(0xFF4ADE80)
                    else -> Color.White
                },
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            val agreedTypes = craftsman.effectiveWorkTypes.filter { it.agreedCount > 0 }
            if (agreedTypes.isNotEmpty()) {
                agreedTypes.forEach { workType ->
                    Text(
                        text = "${workType.type}: ${formatWork(workType.completedCount)}/${formatWork(workType.agreedCount)}" +
                            " × ${formatAmount(workType.unitPrice)} = ${formatAmount(workType.agreedValue)}",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.White.copy(alpha = 0.7f),
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
                Text(
                    text = strings.agreementTotal
                        .fillTemplate("amount" to formatAmount(craftsman.agreedTotalValue)),
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.White.copy(alpha = 0.85f),
                    modifier = Modifier.padding(top = 4.dp),
                )
                val progress = (craftsman.totalCompletedCount / craftsman.totalAgreedCount)
                    .toFloat().coerceIn(0f, 1f)
                LinearProgressIndicator(
                    progress = { progress },
                    color = if (progress >= 1f) Color(0xFF4ADE80) else Color(0xFFFACC15),
                    trackColor = Color.White.copy(alpha = 0.15f),
                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp).height(6.dp),
                )
                Text(
                    text = strings.doneRemaining.fillTemplate(
                        "done" to formatWork(craftsman.totalCompletedCount),
                        "remaining" to formatWork(craftsman.remainingWorkCount),
                        "units" to strings.units,
                    ),
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.White.copy(alpha = 0.7f),
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            HorizontalDivider(
                modifier = Modifier.padding(vertical = 12.dp),
                color = Color.White.copy(alpha = 0.15f),
            )
            SummaryLine(strings.earnedFromWorkLine, formatAmount(balance.earnedFromWork), Color(0xFF4ADE80))
            if (balance.extras > 0) {
                SummaryLine(strings.extraWorkLine, formatAmount(balance.extras), Color(0xFF4ADE80))
            }
            if (balance.unitsGivenValue > 0) {
                SummaryLine(strings.givenUnitValueLine, "−${formatAmount(balance.unitsGivenValue)}", Color(0xFFF87171))
            }
            if (balance.cashPaid > 0) {
                SummaryLine(strings.cashPaidLine, "−${formatAmount(balance.cashPaid)}", Color(0xFFF87171))
            }
            HorizontalDivider(
                modifier = Modifier.padding(vertical = 10.dp),
                color = Color.White.copy(alpha = 0.15f),
            )
            val projected = balance.projectedNet
            SummaryLine(
                label = strings.whenFinishedLine,
                value = when {
                    projected > 0 -> strings.whenFinishedOwed
                        .fillTemplate("amount" to formatAmount(projected))
                    projected < 0 -> strings.whenFinishedOwes
                        .fillTemplate("amount" to formatAmount(abs(projected)))
                    else -> strings.whenFinishedBalanced
                },
                valueColor = Color.White,
            )
        }
    }
}

@Composable
private fun SummaryLine(label: String, value: String, valueColor: Color) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(label, style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.75f))
        Text(value, style = MaterialTheme.typography.bodySmall, color = valueColor, fontWeight = FontWeight.Medium)
    }
}

/** Fatura takibi: kesilmesi gereken / kesilen / kalan. Tap = hedef tutarı düzenle. */
@Composable
private fun InvoiceCard(
    required: Double,
    invoiced: Double,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = strings.invoiceCardTitle,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                )
                if (required > 0 || invoiced > 0) {
                    val remaining = (required - invoiced).coerceAtLeast(0.0)
                    Text(
                        text = if (remaining > 0) {
                            strings.invoiceRemainingShort.fillTemplate("amount" to formatAmount(remaining))
                        } else {
                            strings.completed
                        },
                        style = MaterialTheme.typography.labelMedium,
                        color = if (remaining > 0) AppColors.Reserved else AppColors.Income,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
            if (required <= 0 && invoiced <= 0) {
                Text(
                    text = strings.invoiceCardHint,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                    modifier = Modifier.padding(top = 4.dp),
                )
            } else {
                Text(
                    text = strings.invoiceIssuedOf.fillTemplate(
                        "invoiced" to formatAmount(invoiced),
                        "required" to formatAmount(required),
                    ),
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                    modifier = Modifier.padding(top = 4.dp),
                )
                if (required > 0) {
                    val progress = (invoiced / required).toFloat().coerceIn(0f, 1f)
                    LinearProgressIndicator(
                        progress = { progress },
                        color = if (progress >= 1f) AppColors.Income else AppColors.Reserved,
                        trackColor = AppColors.Outline,
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp).height(6.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun InvoiceTargetDialog(
    currentRequired: Double,
    onDismiss: () -> Unit,
    onSave: (amount: Double) -> Unit,
) {
    var amountText by remember {
        mutableStateOf(if (currentRequired > 0) currentRequired.toLong().toString() else "")
    }
    val strings = LocalStrings.current

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(strings.invoiceDialogTitle, style = MaterialTheme.typography.titleMedium)
                Text(
                    text = strings.invoiceDialogHint,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )
                AmountField(
                    label = strings.amountFieldLabel,
                    value = amountText,
                    onValueChange = { amountText = it },
                    modifier = Modifier.fillMaxWidth(),
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = { onSave(amountText.toLongOrNull()?.toDouble() ?: 0.0) },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.save, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun GivenUnitRow(
    unit: ApartmentUnit,
    projectName: String,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .background(AppColors.Reserved.copy(alpha = 0.16f), RoundedCornerShape(12.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = unit.label,
                    style = MaterialTheme.typography.labelLarge,
                    color = AppColors.Reserved,
                    fontWeight = FontWeight.Bold,
                )
            }
            Column(modifier = Modifier.weight(1f).padding(horizontal = 12.dp)) {
                Text(
                    text = "$projectName • ${unitDisplayLabel(unit.block, unit.number)}",
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = strings.givenAsBarter,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )
            }
            Text(
                text = formatAmount(unit.salePrice ?: 0.0),
                style = MaterialTheme.typography.titleSmall,
                color = AppColors.NavyDeep,
            )
        }
    }
}

@Composable
private fun EntryRow(
    entry: CraftsmanEntry,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    val (typeLabel, amountColor, sign) = when (entry.type) {
        CraftsmanEntryType.EXTRA_WORK -> Triple(strings.extraWorkLine, AppColors.Income, "+")
        CraftsmanEntryType.CASH_PAYMENT -> Triple(strings.pdfCashPayment, AppColors.Expense, "−")
        CraftsmanEntryType.INVOICE -> Triple(strings.entryInvoiceTitle, AppColors.Reserved, "")
    }
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = entry.description,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = "${formatDateShort(entry.dateEpochMillis)}  •  $typeLabel",
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )
            }
            Text(
                text = sign + formatAmount(entry.amount),
                style = MaterialTheme.typography.titleSmall,
                color = amountColor,
            )
        }
    }
}

@Composable
private fun WorkProgressDialog(
    workTypes: List<com.bulut.mecitogullari.data.model.WorkTypeAgreement>,
    onDismiss: () -> Unit,
    onSave: (Map<String, String>) -> Unit,
) {
    val strings = LocalStrings.current
    val texts = remember {
        mutableStateMapOf<String, String>().apply {
            workTypes.forEach { put(it.type, formatWork(it.completedCount)) }
        }
    }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(strings.updateWorkTitle, style = MaterialTheme.typography.titleMedium)
                Text(
                    text = strings.updateWorkHint,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )
                workTypes.forEach { workType ->
                    OutlinedTextField(
                        value = texts[workType.type] ?: "",
                        onValueChange = { value ->
                            texts[workType.type] = value.filter { c -> c.isDigit() || c == ',' }
                        },
                        label = {
                            Text(
                                strings.workTypeAgreedLabel.fillTemplate(
                                    "type" to workType.type,
                                    "count" to formatWork(workType.agreedCount),
                                )
                            )
                        },
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = { onSave(texts.toMap()) },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.save, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun CraftsmanEntryDialog(
    onDismiss: () -> Unit,
    onSave: (type: CraftsmanEntryType, amount: Double, dateEpochMillis: Long, description: String) -> Unit,
) {
    var type by remember { mutableStateOf(CraftsmanEntryType.CASH_PAYMENT) }
    var amountText by remember { mutableStateOf("") }
    var date by remember { mutableStateOf(todayUtcMidnightMillis()) }
    var description by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    val strings = LocalStrings.current

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(
                    text = when (type) {
                        CraftsmanEntryType.CASH_PAYMENT -> strings.entryCashTitle
                        CraftsmanEntryType.EXTRA_WORK -> strings.entryExtraTitle
                        CraftsmanEntryType.INVOICE -> strings.entryInvoiceTitle
                    },
                    style = MaterialTheme.typography.titleMedium,
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp))
                        .padding(4.dp),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    TypeOption(strings.entryTypeCash, type == CraftsmanEntryType.CASH_PAYMENT, AppColors.Expense, Modifier.weight(1f)) {
                        type = CraftsmanEntryType.CASH_PAYMENT
                        error = null
                    }
                    TypeOption(strings.entryTypeExtra, type == CraftsmanEntryType.EXTRA_WORK, AppColors.Income, Modifier.weight(1f)) {
                        type = CraftsmanEntryType.EXTRA_WORK
                        error = null
                    }
                    TypeOption(strings.entryTypeInvoice, type == CraftsmanEntryType.INVOICE, AppColors.Reserved, Modifier.weight(1f)) {
                        type = CraftsmanEntryType.INVOICE
                        error = null
                    }
                }

                DateField(
                    label = strings.dateLabel,
                    dateEpochMillis = date,
                    onDateSelected = { date = it },
                    modifier = Modifier.fillMaxWidth(),
                )

                AmountField(
                    label = strings.amountFieldLabel,
                    value = amountText,
                    onValueChange = {
                        amountText = it
                        error = null
                    },
                    modifier = Modifier.fillMaxWidth(),
                )

                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = {
                        Text(
                            when (type) {
                                CraftsmanEntryType.CASH_PAYMENT -> strings.descCashHint
                                CraftsmanEntryType.EXTRA_WORK -> strings.descExtraHint
                                CraftsmanEntryType.INVOICE -> strings.descInvoiceHint
                            }
                        )
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )

                Text(
                    text = when (type) {
                        CraftsmanEntryType.CASH_PAYMENT -> strings.hintCashPayment
                        CraftsmanEntryType.EXTRA_WORK -> strings.hintExtraWork
                        CraftsmanEntryType.INVOICE -> strings.hintInvoice
                    },
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )

                error?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = AppColors.Expense)
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = {
                            val amount = amountText.toLongOrNull()?.toDouble()
                            if (amount == null || amount <= 0.0) {
                                error = strings.enterValidAmount
                                return@Button
                            }
                            onSave(type, amount, date, description)
                        },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.save, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun GiveUnitDialog(
    projects: List<com.bulut.mecitogullari.data.model.Project>,
    availableUnits: List<ApartmentUnit>,
    onDismiss: () -> Unit,
    onGive: (unit: ApartmentUnit, valueTl: Double) -> Unit,
) {
    var projectId by remember { mutableStateOf<String?>(null) }
    var unitId by remember { mutableStateOf<String?>(null) }
    var valueText by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    val strings = LocalStrings.current

    val projectUnits = availableUnits.filter { it.projectId == projectId }.sortedBy { it.number }
    val unitLabels = projectUnits.map { unitDisplayLabel(it.block, it.number) }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(strings.giveUnitTitle, style = MaterialTheme.typography.titleMedium)
                Text(
                    text = strings.giveUnitHint,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )

                DropdownField(
                    label = strings.project,
                    value = projects.firstOrNull { it.id == projectId }?.name ?: "",
                    options = projects.map { it.name },
                    onSelect = { selected ->
                        projectId = projects.firstOrNull { it.name == selected }?.id
                        unitId = null
                    },
                )

                if (projectId != null) {
                    DropdownField(
                        label = strings.unitLabel,
                        value = projectUnits.firstOrNull { it.id == unitId }
                            ?.let { unitDisplayLabel(it.block, it.number) } ?: "",
                        options = unitLabels,
                        onSelect = { selected ->
                            val index = unitLabels.indexOf(selected)
                            unitId = projectUnits.getOrNull(index)?.id
                        },
                    )
                }

                AmountField(
                    label = strings.unitValueLabel,
                    value = valueText,
                    onValueChange = {
                        valueText = it
                        error = null
                    },
                    modifier = Modifier.fillMaxWidth(),
                )

                error?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = AppColors.Expense)
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = {
                            val unit = projectUnits.firstOrNull { it.id == unitId }
                            val value = valueText.toLongOrNull()?.toDouble()
                            when {
                                unit == null -> error = strings.selectUnitError
                                value == null || value <= 0.0 -> error = strings.enterUnitValueError
                                else -> onGive(unit, value)
                            }
                        },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.give, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun TypeOption(
    text: String,
    isSelected: Boolean,
    activeColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Row(
        modifier = modifier
            .background(
                color = if (isSelected) activeColor else Color.Transparent,
                shape = RoundedCornerShape(9.dp),
            )
            .clickable(onClick = onClick)
            .padding(vertical = 10.dp),
        horizontalArrangement = Arrangement.Center,
    ) {
        Text(
            text = text,
            color = if (isSelected) Color.White else AppColors.TextSecondary,
            style = MaterialTheme.typography.labelLarge,
            maxLines = 1,
        )
    }
}
